import { PropsWithChildren, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { z } from 'zod';
import Button from '~/components/Button';
import Icon from '~/components/Icon';
import MediaDisplay, { MediaFallback } from '~/components/MediaDisplay';
import {
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '~/components/Modal';
import Spinner from '~/components/Spinner';
import api from '~/utils/api';
import { uniqueId, useUniqueId } from '~/utils/compute';
import { disjunctiveList } from '~/utils/formatters';
import { logError } from '~/utils/observability';
import { mediaAssetSchema } from '~/utils/schemas';
import Field from './Field';

type UploadFilesParams = Parameters<typeof api.uploadFile>[0];
type APIUploadResult = Awaited<ReturnType<typeof api.uploadFile>>;
type MediaAsset = z.infer<typeof mediaAssetSchema>;
type BaseAsset = Omit<MediaAsset, 'cdnName'> & { id: string };

type UploadedAsset = BaseAsset & {
  status: 'uploaded';
  /** The original filename. */
  name: string;
  /** The filename as uploaded to the CDN. */
  cdnName: string;
};
type PreviewAsset = Omit<BaseAsset, 'src'> & {
  status: 'preview';
  file: File;
};
type ErrorAsset = Omit<PreviewAsset, 'status'> & {
  status: 'error';
  error: Error;
};
type InvalidAsset = Omit<ErrorAsset, 'status'> & {
  status: 'invalid';
};
type Asset = UploadedAsset | PreviewAsset | ErrorAsset | InvalidAsset;

const defaultAcceptTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'video/mp4',
  'video/webm',
  'application/pdf',
  'video/quicktime',
] as const;
type AcceptType = (typeof defaultAcceptTypes)[number];
const acceptTypeLabels = {
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'video/mp4': 'MP4',
  'video/webm': 'WebM',
  'application/pdf': 'PDF',
  'video/quicktime': 'MOV',
} satisfies Record<AcceptType, string>;

type DropzoneProps = PropsWithChildren<{
  onChange: (files: FileList) => void;
  accept: AcceptType[] | Readonly<AcceptType[]>;
  required?: boolean;
  describedby?: string;
  id: string;
  invalid?: boolean;
}>;

function Dropzone({
  required,
  onChange,
  accept,
  describedby,
  id,
  invalid,
  children,
}: DropzoneProps) {
  const dropzoneId = useUniqueId();
  return (
    <div
      id={dropzoneId}
      className={twMerge(
        'relative flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-outline bg-gray-50 p-4 text-sm text-gray-500 focus-within:border-primary focus-within:bg-white hover:bg-gray-100',
        invalid && 'border-error focus:border-error focus:outline-error',
      )}
    >
      <Icon icon="CloudUploadSolid" className="size-10" />
      <span className="ml-3">
        Drag and drop your files here or click to browse
      </span>

      {children}
      {required && <span className="sr-only">required</span>}
      <input
        // browsers put this role on, but seems like jsdom doesn't
        role="button"
        data-testid="fileInput"
        id={id}
        aria-describedby={`${describedby} ${dropzoneId}`}
        multiple
        className="absolute inset-0 cursor-pointer opacity-0"
        type="file"
        accept={accept.join(', ')}
        onDrop={(event) => {
          event.preventDefault();
          if (event.dataTransfer?.files) {
            onChange(event.dataTransfer.files);
          }
        }}
        onChange={(event) => {
          const { target } = event;

          if (target.files) {
            onChange(target.files);
            target.value = ''; // reset the input value to allow re-uploading the same file
            target.files = null; // clear the files to avoid re-triggering the change event
          }
        }}
      />
    </div>
  );
}

type AssetDisplayProps = {
  asset: Asset;
  onRemove: (asset: Asset) => void;
  onRetry: (asset: ErrorAsset) => void;
};

function AssetItem({ asset, onRemove, onRetry }: AssetDisplayProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const closeConfirm = () => setShowConfirm(false);
  const removeAsset = () => {
    onRemove(asset);
    closeConfirm();
  };

  return (
    <li className="flex items-center justify-between gap-2">
      <figure className="flex min-w-0 items-center gap-2 md:gap-4">
        {match(asset)
          .with({ status: 'uploaded' }, (a) => (
            <MediaDisplay
              expandable
              src={a.src}
              type={a.type}
              className="size-8 md:size-12"
              alt={a.name}
            />
          ))
          .otherwise((a) => (
            <MediaFallback
              type={a.file.type}
              className="size-8 md:size-12"
              alt={asset.name}
            />
          ))}

        <figcaption className="flex min-w-0 flex-col gap-1">
          <div className="truncate text-sm text-gray-700">{asset.name}</div>
          {(asset.status === 'error' || asset.status === 'invalid') && (
            <div role="alert" className="flex gap-1 text-xs text-error">
              <Icon icon="ExclamationCircleSolid" className="mt-0.5 size-4" />
              {asset.error.message}
            </div>
          )}
        </figcaption>
      </figure>

      {match(asset)
        .with({ status: 'preview' }, (previewAsset) => (
          <Spinner
            className="size-9 fill-black p-1"
            description={`Uploading ${previewAsset.name}`}
          />
        ))
        .with(
          { status: 'uploaded' },
          { status: 'invalid' },
          (uploadedAsset) => (
            <Button variant="icon" onClick={() => setShowConfirm(true)}>
              <Icon
                icon="Trash"
                className="size-5"
                title={`Remove ${uploadedAsset.name}`}
              />
            </Button>
          ),
        )
        .with({ status: 'error' }, (errorAsset) => (
          <Button variant="icon" onClick={() => onRetry(errorAsset)}>
            <Icon icon="ArrowsRotate" className="size-5" title="Retry" />
          </Button>
        ))
        .exhaustive()}

      <Modal open={showConfirm} onClose={closeConfirm}>
        <ModalHeader>
          <ModalTitle>Remove File</ModalTitle>
        </ModalHeader>
        <p className="text-sm">
          Do you want to remove the file{' '}
          <span className="font-mono font-semibold">{asset.name}</span>?
        </p>
        <ModalFooter>
          <Button variant="outlined" onClick={closeConfirm}>
            Keep
          </Button>
          <Button className="flex items-center gap-2" onClick={removeAsset}>
            <Icon icon="Trash" /> Remove
          </Button>
        </ModalFooter>
      </Modal>
    </li>
  );
}

const uploadFile =
  (params: {
    storefrontId: UploadFilesParams['params']['storefrontId'];
    destination: UploadFilesParams['query']['destination'];
    onSuccess: (params: {
      asset: PreviewAsset;
      result: APIUploadResult;
    }) => void;
    onError: (params: { asset: PreviewAsset; result: Error }) => void;
  }) =>
  (asset: PreviewAsset) => {
    api
      .uploadFile({
        params: { storefrontId: params.storefrontId },
        query: { destination: params.destination },
        body: asset.file,
      })
      .then((result) => {
        params.onSuccess({ asset, result });
      })
      .catch((e) => {
        const error =
          e instanceof Error ? e : new Error('Upload failed', { cause: e });

        logError(error, { extra: { name: asset.name, type: asset.type } });

        params.onError({
          asset,
          result: new Error('Failed to upload file'),
        });
      });
  };

const upsertAsset = (assets: Asset[], asset: Asset) => {
  const next = [...assets];
  const index = next.findIndex((a) => a.id === asset.id);

  if (index !== -1) {
    next[index] = asset;
  } else {
    next.push(asset);
  }

  return next;
};

const fileMessaging = (count: number) =>
  `${count} ${count === 1 ? 'file' : 'files'}`;

type State = {
  assets: Asset[];
  isValid: boolean;
  uploadedCount: number;
};

type Props = {
  label: string;
  className?: string;
  name: string;
  storefrontId: UploadFilesParams['params']['storefrontId'];
  destination: UploadFilesParams['query']['destination'];
  requiredCount?: number;
  defaultValue?: Omit<UploadedAsset, 'id' | 'status'>[];
  description?: string;
  accept?: AcceptType[] | Readonly<AcceptType[]>;
};

export default function FileUploader({
  label,
  className,
  name,
  storefrontId,
  destination,
  requiredCount = 0,
  defaultValue = [],
  description,
  accept = defaultAcceptTypes,
}: Props) {
  const [state, setState] = useState<State>({
    assets: defaultValue.map((asset) => ({
      ...asset,
      // give existing asset an id for display list
      id: uniqueId(),
      status: 'uploaded',
    })),
    isValid: true,
    uploadedCount: defaultValue.length,
  });

  const descriptionId = useUniqueId();
  const errorId = useUniqueId();
  const dropzoneId = useUniqueId();
  const requiredCountValidityRef = useRef<HTMLInputElement>(null);

  const handleAssetChange = (
    asset: Asset,
    action: 'upsert' | 'remove' = 'upsert',
  ) => {
    setState((prev) => {
      const updatedAssets =
        action === 'upsert' ?
          upsertAsset(prev.assets, asset)
        : prev.assets.filter((a) => a.id !== asset.id);

      const uploadedCount = updatedAssets.filter(
        (a) => a.status === 'uploaded',
      ).length;

      // updating the validity on asset change only when the state is invalid to provide a delayed validation until submit
      const isValid =
        !prev.isValid ? uploadedCount >= requiredCount : prev.isValid;
      // reset validity if invalid and now valid
      if (!prev.isValid && isValid && requiredCountValidityRef.current) {
        requiredCountValidityRef.current.setCustomValidity('');
      }

      return {
        assets: updatedAssets,
        isValid,
        uploadedCount,
      } satisfies State;
    });
  };

  const uploadAsset = uploadFile({
    storefrontId,
    destination,
    onSuccess: ({ asset: { file, ...asset }, result }) => {
      handleAssetChange({
        id: asset.id,
        type: asset.type,
        src: result.src,
        name: asset.name,
        cdnName: result.name,
        status: 'uploaded',
      } satisfies UploadedAsset);
    },
    onError: ({ asset, result }) =>
      handleAssetChange({
        ...asset,
        status: 'error',
        error: result,
      } satisfies ErrorAsset),
  });

  const onUpload = (fileList: FileList) => {
    Array.from(fileList).forEach((file) => {
      const asset = {
        id: uniqueId(),
        name: file.name,
        type: file.type,
        file,
        status: 'preview',
      } satisfies PreviewAsset;

      if (accept.includes(file.type as AcceptType)) {
        // add asset to state to display preview while uploaded
        handleAssetChange(asset);
        uploadAsset(asset);
      } else {
        handleAssetChange({
          ...asset,
          status: 'invalid',
          error: new Error(
            `File not accepted. Accepted file types: ${disjunctiveList(accept.map((type) => acceptTypeLabels[type]))}.`,
          ),
        });
      }
    });
  };

  const removeAsset = (asset: Asset) => {
    handleAssetChange(asset, 'remove');
  };

  const retryUpload = ({ error, ...rest }: ErrorAsset) => {
    const retryAsset = {
      ...rest,
      status: 'preview',
    } satisfies PreviewAsset;

    handleAssetChange(retryAsset);
    uploadAsset(retryAsset);
  };

  const hasMetRequiredCount = state.uploadedCount >= requiredCount;

  return (
    <Field.Root className={className}>
      <Field.Label htmlFor={dropzoneId}>
        {label} {requiredCount === 0 && ` (optional)`}
      </Field.Label>
      {description && (
        <Field.HelpText className="mb-1" id={descriptionId}>
          {description}
        </Field.HelpText>
      )}

      <Dropzone
        onChange={onUpload}
        required={requiredCount > 0}
        accept={accept}
        id={dropzoneId}
        describedby={[descriptionId, errorId].join(' ')}
      >
        {requiredCount > 0 && (
          <span className={state.isValid ? '' : 'text-error'}>
            {hasMetRequiredCount ?
              `${fileMessaging(state.uploadedCount)} uploaded`
            : `${state.uploadedCount} of ${`${fileMessaging(requiredCount)} uploaded`}`
            }
          </span>
        )}
      </Dropzone>
      {requiredCount > 0 && !state.isValid && (
        <Field.ErrorText id={errorId} className="mt-1">
          {`Please upload at least ${fileMessaging(requiredCount)}.`}
        </Field.ErrorText>
      )}

      <input
        id="requiredCountValidity"
        ref={requiredCountValidityRef}
        type="number"
        // using hidden attribute will prevent input from participating in validation aria-hidden will remove it from the accessibility tree
        aria-hidden
        tabIndex={-1}
        className="sr-only"
        min={requiredCount}
        onInvalid={(event) => {
          event.currentTarget.setCustomValidity(
            `Please upload at least ${fileMessaging(requiredCount)}.`,
          );
          setState((prev) => ({
            ...prev,
            isValid: false,
          }));
        }}
        value={state.uploadedCount}
        // noop to avoid uncontrolled input warning
        onChange={() => {}}
      />
      {state.assets.length > 0 && (
        // * using a single column grid to handle long filenames with truncation better in the available space
        <ul className="grid w-full grid-cols-1 divide-y divide-outline *:py-2">
          {state.assets.map((asset) => (
            <AssetItem
              key={asset.id}
              asset={asset}
              onRemove={removeAsset}
              onRetry={retryUpload}
            />
          ))}
        </ul>
      )}
      {state.assets
        .filter((a) => a.status === 'uploaded')
        .map(({ id, status, ...asset }) => (
          <input
            key={id}
            hidden
            name={name}
            defaultValue={JSON.stringify(asset satisfies MediaAsset)}
          />
        ))}
    </Field.Root>
  );
}
